import { useCallback, useSyncExternalStore } from "react";

const CHANGE_EVENT = "rovero-appearance-change";
export const appearanceKey = (account: string | number, preference: string) =>
  `RoveroCRM.appearance.${account}.${preference}`;

export const appearanceAccount = (id: string | number | undefined) =>
  id === undefined || id === "" ? "guest" : id;

export function readAppearance<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
  legacyKey?: string,
): T {
  try {
    const raw =
      window.localStorage.getItem(key) ??
      (legacyKey ? window.localStorage.getItem(legacyKey) : null);
    if (raw === null) return fallback;
    const value = JSON.parse(raw);
    return allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
}

export function saveAppearance<T extends string>(key: string, value: T) {
  window.localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function migrateLegacyTheme(
  account: string | number,
  storageKey: string,
) {
  const key = appearanceKey(account, storageKey);
  const marker = "RoveroCRM.appearance.legacy-theme-owner";
  if (account === "guest" || window.localStorage.getItem(marker)) return;
  const legacy = window.localStorage.getItem(`RaStoreCRM.${storageKey}`);
  if (!legacy) return;
  if (window.localStorage.getItem(key)) {
    window.localStorage.setItem(marker, String(account));
    return;
  }
  try {
    const value = JSON.parse(legacy);
    if (!["system", "light", "dark"].includes(value)) return;
    saveAppearance(key, value);
    window.localStorage.setItem(marker, String(account));
  } catch {
    /* Ignore invalid legacy settings. */
  }
}

export function usePersistentAppearance<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
  legacyKey?: string,
): [T, (value: T) => void] {
  const subscribe = useCallback((notify: () => void) => {
    window.addEventListener("storage", notify);
    window.addEventListener(CHANGE_EVENT, notify);
    return () => {
      window.removeEventListener("storage", notify);
      window.removeEventListener(CHANGE_EVENT, notify);
    };
  }, []);
  const value = useSyncExternalStore(
    subscribe,
    () => readAppearance(key, fallback, allowed, legacyKey),
    () => fallback,
  );
  const setValue = useCallback(
    (next: T) => {
      if (allowed.includes(next)) saveAppearance(key, next);
    },
    [key, allowed],
  );
  return [value, setValue];
}
